import { graphql } from 'gatsby';
import { RichText } from 'prismic-reactjs';
import * as React from 'react';

import PrimaryLayout from '../layouts/PrimaryLayout';
import PageIntro from '../components/layout/PageIntro';
import PropertyPanels from '../components/layout/PropertyPanels';
import { IPrismicComponentNode } from '../components/utils/declarations';
import htmlSerializer from '../components/layout/Prismic/PrismicRender';
import {
  prismicComponentList,
  prismicComponentDemos,
} from '../components/layout/Prismic/prismictemplateparts';
import { useSubnav } from '../components/providers/SubnavProvider';
import usePrismicComponentBody, {
  IPrismicComponentBody,
} from '../components/hooks/usePrismicComponentBody';
import usePrismicComponentHeadings from '../components/hooks/usePrismicComponentHeadings';

interface IWebTemplateProps {
  data: {
    prismic: {
      allWebs: {
        edges: [
          {
            node: IPrismicComponentNode;
          },
        ];
      };
    };
  };
}

const WebTemplate: React.FunctionComponent<IWebTemplateProps> = ({ data }) => {
  // Required check for no data being returned
  const doc = data.prismic.allWebs.edges.slice(0, 1).pop();
  if (!doc) {
    return null;
  }

  const { updateSubnav } = useSubnav();

  const { title, intro, expanded_intro, body } = doc.node;
  const { uid, tags } = doc.node._meta;
  const slug = `/web/components/${tags[0]}/${uid}`;

  React.useEffect(() => {
    const demos: IPrismicComponentBody[] = usePrismicComponentBody(body, 'component_demo') || [];
    const subnavItems = usePrismicComponentHeadings(demos);
    updateSubnav(subnavItems);
  }, []);

  const pageTitle = title && RichText.asText(title);
  const components = body && prismicComponentList(body);

  return (
    <PrimaryLayout title={pageTitle || 'Prismic Preview'} path={slug}>
      <PageIntro
        title={pageTitle || 'Prismic Preview'}
        intro={intro ? RichText.asText(intro) : undefined}
        path={slug}
      />

      {expanded_intro && (
        <div>
          <RichText render={expanded_intro} htmlSerializer={htmlSerializer} />
        </div>
      )}

      {body && prismicComponentDemos(body, 'web')}

      {components && (
        <>
          <PropertyPanels components={components} platform="web" />
        </>
      )}
    </PrimaryLayout>
  );
};

export default WebTemplate;

export const query = graphql`
  query WebTemplateQuery($uid: String, $tag: [String!]) {
    prismic {
      allWebs(uid: $uid, tags: $tag) {
        edges {
          node {
            title
            intro
            expanded_intro
            body {
              ... on PRISMIC_WebBodyComponent_list {
                type
                fields {
                  component
                }
              }
              ... on PRISMIC_WebBodyComponent_demo {
                type
                primary {
                  demo_description
                  demo_title
                }
                fields {
                  demo_code
                  demo_summary
                }
              }
            }
            _meta {
              uid
              tags
            }
          }
        }
      }
    }
  }
`;
